import { FC } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SignOff, TradingCounterParty } from "../../../../api/Api";
import {
  counterpartyKeys,
  useUpdateCounterparty
} from "../../dataHooks/counterpartyQueries";
import { SignOffPageLayout } from "../../../../components/lifecycle/signOff/signOffPage";
import { SignOffDetailsPanel } from "./signOffDetailsPanel";
import { buildSignOffPdf } from "./buildSignOffPdf";

type Props = {
  specificCounterparty: TradingCounterParty;
};

export const SignOffPage: FC<Props> = ({ specificCounterparty }) => {
  const updateCounterparty = useUpdateCounterparty();
  const queryClient = useQueryClient();

  const persist = (
    signOff: SignOff,
    handlers: { onError: (e: Error) => void; onSuccess: () => void }
  ) => {
    updateCounterparty.mutate(
      { ...specificCounterparty, signOff },
      {
        onError: (e) => handlers.onError(e as Error),
        onSuccess: () => {
          handlers.onSuccess();
          // The backend may have mutated the sign-off (e.g. flipping status to "completed" once
          // all signatories are signed). The shared useUpdateCounterparty hook seeds the cache
          // with the request body, so without an explicit invalidation we'd keep showing our
          // optimistic state until the user reloads.
          if (specificCounterparty.id) {
            queryClient.invalidateQueries({
              queryKey: counterpartyKeys.specific(specificCounterparty.id)
            });
          }
        }
      }
    );
  };

  const handleDownloadPdf = (displayedSignOff?: SignOff | null) => {
    const doc = buildSignOffPdf({
      ...specificCounterparty,
      signOff: displayedSignOff ?? specificCounterparty.signOff
    });
    const safeName = (
      specificCounterparty.counterpartyName || "counterparty"
    ).replace(/[^a-z0-9]/gi, "_");
    doc.save(`signoff_${safeName}.pdf`);
  };

  return (
    <SignOffPageLayout
      entityName={specificCounterparty.counterpartyName}
      signOff={specificCounterparty.signOff}
      vp={specificCounterparty.vp}
      saving={updateCounterparty.isPending}
      persist={persist}
      detailsPanel={(extraSections) => (
        <SignOffDetailsPanel
          counterparty={specificCounterparty}
          extraSections={extraSections}
        />
      )}
      onDownloadPdf={handleDownloadPdf}
      withOperationalReadiness
    />
  );
};
